import { ICreateDiagnosticsPackagesDto, IDiagnosticsPackages, IUpdateDiagnosticsPackagesDto } from "types/models/DiagnosticsPackages";
import { BaseStoreClass, IBaseStoreClass } from "./BaseStoreClass";
import { INotificationsStore } from "./NotificationsStore";
import { IDiagnosticsPackagesService } from "services/DiagnosticsPackagesService";
import { action, makeObservable, observable } from "mobx";
import { getErrorMessage, storeResponseHandler } from "utilities/ErrorHelper";
import { t } from "i18next";

export interface IDiagnosticsPackagesStore extends IBaseStoreClass {
    diagnosticsPackages: IDiagnosticsPackages[];
    diagnosticsPackagesTags: string[];
    currentSelectedDiagnosticsPackages: IDiagnosticsPackages | null;

    isLoadingDiagnosticsPackages: boolean;
    isFormLoading: boolean;

    isBusy: boolean;

    isRemovingDiagnosticsPackage: boolean;

    currentFormResponseUrl: string | null;
    hasFormResponse: boolean;

    setCurrentSelectedDiagnosticsPackage(diagnosticsPackages: IDiagnosticsPackages | null): void;
    getDiagnosticsPackages(): void;
}

export class DiagnosticsPackagesStore extends BaseStoreClass implements IDiagnosticsPackagesStore {
    diagnosticsPackages: IDiagnosticsPackages[] = [];
    diagnosticsPackagesTags: string[] = [];
    currentSelectedDiagnosticsPackages: IDiagnosticsPackages | null = null;
    immutableSelectedDiagnosticsPackages: IDiagnosticsPackages | null = null;

    isLoadingDiagnosticsPackages: boolean = false;
    isFormLoading: boolean = false;
    isRemovingDiagnosticsPackage: boolean = false;

    isBusy: boolean = this.isLoadingDiagnosticsPackages && this.isFormLoading && this.isRemovingDiagnosticsPackage;

    currentFormResponseUrl: string | null;
    hasFormResponse: boolean = false;

    _notificationsStore: INotificationsStore;
    _diagnosticsPackagesService: IDiagnosticsPackagesService;

    constructor(
        diagnosticsPackagesService: IDiagnosticsPackagesService,
        notificationsStore: INotificationsStore) {
        super();

        this.take = 100;

        this._notificationsStore = notificationsStore;
        this._diagnosticsPackagesService = diagnosticsPackagesService;

        makeObservable(this, {
            diagnosticsPackages: observable,
            diagnosticsPackagesTags: observable,
            isLoadingDiagnosticsPackages: observable,
            isFormLoading: observable,
            isRemovingDiagnosticsPackage: observable,
            currentFormResponseUrl: observable,
            hasFormResponse: observable,
            isBusy: observable,
            currentSelectedDiagnosticsPackages: observable,
            setCurrentSelectedDiagnosticsPackage: action,
            getDiagnosticsPackages: action,
        });
    }

    async getDiagnosticsPackages() {
        try {
            this.setIsLoadingDiagnosticsPackages(true);

            const diagnosticsPackagesResponse = await this._diagnosticsPackagesService.getAllDiagnosticsPackages({
                page: this.page - 1, //Backend Pagination starts from 0
                pageSize: this.take,
                updatedAfter: "2026-01-01",
                includeDeleted: false
            });

            let diagnosticsPackagesDtoData: IDiagnosticsPackages[] = diagnosticsPackagesResponse.data;
            if (!diagnosticsPackagesDtoData || diagnosticsPackagesResponse.isError) {
                return;
            }

            let enrichedDiagnosticsPackages = this.enrichDiagnosticsPackages(diagnosticsPackagesDtoData);

            let destictTags = Array.from(new Set(enrichedDiagnosticsPackages.flatMap(item => item.packageTags)));

            this.setDiagnosticsPackages(enrichedDiagnosticsPackages);
            this.setDiagnosticsPackagesTags(destictTags);
        } catch (error: any) {
            this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
        } finally {
            this.setIsLoadingDiagnosticsPackages(false);
        }
    }

    public createDiagnosticsPackage = async (
        name: string,
        description: string | null,
        packageTag: string,
        trackingDiagnosticsEntryIds: string[],
        CB?: () => void) => {
        this.setIsFormLoading(true);
        let reponseResult: string | null = null;
        try {

            const request: ICreateDiagnosticsPackagesDto = {
                name: name,
                description: description,
                packageTags: [packageTag],
                trackingDiagnosticsEntries: trackingDiagnosticsEntryIds
            }

            const response = await this._diagnosticsPackagesService.createDiagnosticsPackage(request);
            storeResponseHandler(response, t('DiagnosticsPackages.Action.CreateDiagnosticsPackage.Successful'), this._notificationsStore.addToast);

            if (response.data && !response.isError) {
                reponseResult = response.data.id;
                this.getDiagnosticsPackages();
                const diagnosticsPackageUrl = await this._diagnosticsPackagesService.getDiagnosticsPackageUrl(reponseResult);
                this.setCurrentFormResponseUrl(true, diagnosticsPackageUrl);
                if (CB) CB();
            }
        } catch (error: any) {
            this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
        } finally {
            this.setIsFormLoading(false);
        }
    };

    public patchEntriesDiagnosticsPackage = async (
        id: string,
        trackingDiagnosticsEntryIds: string[],
        CB?: () => void) => {
        if (!id || id === '' || !trackingDiagnosticsEntryIds || trackingDiagnosticsEntryIds.length === 0) {
            return;
        }

        this.setIsFormLoading(true);
        try {

            const foundDiagnosticsPackage = this.diagnosticsPackages.find(x => x.id === id);
            if (foundDiagnosticsPackage == null) {
                this._notificationsStore.addToast('Error', t('DiagnosticsPackages.Error.PackageNotFound'), 'error');
                return;
            }

            const combinedUniqueEntries = Array.from(new Set([...(trackingDiagnosticsEntryIds || []), ...(foundDiagnosticsPackage.trackingDiagnosticsEntries || [])]));

            const request: IUpdateDiagnosticsPackagesDto = {
                name: foundDiagnosticsPackage.name,
                description: foundDiagnosticsPackage.description,
                packageTags: foundDiagnosticsPackage.packageTags,
                trackingDiagnosticsEntries: combinedUniqueEntries
            }

            this.handlePatchRequest(id, request, CB);
        } catch (error: any) {
            this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
        } finally {
            this.setIsFormLoading(false);
        }
    };

    public patchDiagnosticsPackage = async (
        name: string,
        description: string | null,
        packageTag: string,
        trackingDiagnosticsEntryIds: string[],
        CB?: () => void) => {
        const id = this.currentSelectedDiagnosticsPackages?.id
        if (!id) {
            return;
        }

        this.setIsFormLoading(true);
        try {

            const request: IUpdateDiagnosticsPackagesDto = {
                name: name,
                description: description,
                packageTags: [packageTag],
                trackingDiagnosticsEntries: trackingDiagnosticsEntryIds
            }

            this.handlePatchRequest(id, request, CB);
        } catch (error: any) {
            this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
        } finally {
            this.setIsFormLoading(false);
        }
    };

    public handlePatchRequest = async (id: string, request: IUpdateDiagnosticsPackagesDto, CB?: () => void) => {
        const response = await this._diagnosticsPackagesService.patchDiagnosticsPackage(id, request);
        storeResponseHandler(response, t('DiagnosticsPackages.Action.UpdateDiagnosticsPackage.Successful'), this._notificationsStore.addToast);

        if (!response.isError) {
            this.getDiagnosticsPackages();
            const diagnosticsPackageUrl = await this._diagnosticsPackagesService.getDiagnosticsPackageUrl(id);
            this.setCurrentFormResponseUrl(true, diagnosticsPackageUrl);
            if (CB) CB();
        }
    }

    public removeDiagnosticsPackage = async (
        id: string,
        CB?: () => void) => {
        if (!id) {
            return;
        }

        this.setIsRemovingDiagnosticsPackage(true);
        try {
            const response = await this._diagnosticsPackagesService.deleteOne(id);
            if (!response.isError) {
                this.getDiagnosticsPackages();
                if (CB) CB();
            }
        } catch (error: any) {
            this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
        } finally {
            this.setIsRemovingDiagnosticsPackage(false);
        }
    };

    public resetCreationResponse = () => {
        this.setCurrentFormResponseUrl(false, null);
    };

    // =========================== UTILS ===========================

    updateIsBusy() {
        this.isBusy = this.isLoadingDiagnosticsPackages || this.isFormLoading || this.isRemovingDiagnosticsPackage;
    }

    isSelectedDiagnosticsPackageChanged(diagnosticsPackageChanges: Partial<IDiagnosticsPackages> = {}) {
        return JSON.stringify({ ...this.currentSelectedDiagnosticsPackages, ...diagnosticsPackageChanges }) !== JSON.stringify(this.immutableSelectedDiagnosticsPackages);
    }

    setIsLoadingDiagnosticsPackages(status: boolean) {
        this.isLoadingDiagnosticsPackages = status;
        this.updateIsBusy()
    }

    setIsFormLoading(status: boolean) {
        this.isFormLoading = status;
        this.updateIsBusy()
    }

    setIsRemovingDiagnosticsPackage(status: boolean) {
        this.isRemovingDiagnosticsPackage = status;
        this.updateIsBusy()
    }

    setDiagnosticsPackages(diagnosticsPackages: IDiagnosticsPackages[]) {
        this.diagnosticsPackages = diagnosticsPackages;
    }

    setDiagnosticsPackagesTags(types: string[]) {
        this.diagnosticsPackagesTags = types;
    }

    setCurrentSelectedDiagnosticsPackage = (diagnosticsPackages: IDiagnosticsPackages | null) => {
        this.currentSelectedDiagnosticsPackages = diagnosticsPackages;
        this.immutableSelectedDiagnosticsPackages = diagnosticsPackages;
        this.resetCreationResponse();
    };

    enrichDiagnosticsPackages = (diagnosticsPackages: IDiagnosticsPackages[] | null): IDiagnosticsPackages[] => {
        if (!diagnosticsPackages || diagnosticsPackages.length === 0) return [];

        return diagnosticsPackages.map(dp => {
            const associated = dp.trackingDiagnosticsEntries || [];
            return {
                ...dp,
                entryCount: associated.length
            };
        });
    };

    setCurrentFormResponseUrl(status: boolean, diagnosticsPackageId: string | null) {
        this.hasFormResponse = status;
        this.currentFormResponseUrl = diagnosticsPackageId;
    }
}